// The single source of truth for every desktop-shell nav surface: the desktop
// icon grid, the taskbar, the Start Menu, and the mobile bottom nav all render
// off these arrays. One registry entry per top-level SECTION — route-variant
// switching (list/new/edit/view) happens inside the section's one window via the
// existing React Router <Outlet/>, not as separate windows.

export const SECTIONS = [
  { id: "dashboard", title: "Dashboard", icon: "🏠", route: "/dashboard", defaultRect: { x: 24, y: 16, width: 940, height: 600 } },
  { id: "quotes", title: "Quotes", icon: "📄", route: "/quotes", defaultRect: { x: 40, y: 28, width: 940, height: 600 } },
  { id: "invoices", title: "Invoices", icon: "🧾", route: "/invoices", defaultRect: { x: 56, y: 40, width: 940, height: 600 } },
  { id: "expenses", title: "Expenses", icon: "💳", route: "/expenses", defaultRect: { x: 72, y: 52, width: 940, height: 600 } },
  { id: "reports", title: "Reports", icon: "📊", route: "/reports", defaultRect: { x: 88, y: 64, width: 940, height: 600 } },
  { id: "orders", title: "Orders", icon: "📦", route: "/orders", defaultRect: { x: 104, y: 76, width: 940, height: 600 } },
  { id: "settings", title: "Settings", icon: "⚙️", route: "/settings", defaultRect: { x: 120, y: 88, width: 860, height: 600 } },
  { id: "admin", title: "Admin", icon: "👤", route: "/admin", adminOnly: true, defaultRect: { x: 136, y: 100, width: 940, height: 600 } },
];

// Lightweight quick-launch shortcuts — no window state of their own; activating
// one just navigates to the sub-route, which opens the parent section's window.
export const QUICK_LAUNCH = [
  { id: "new-quote", title: "New Quote", icon: "➕", route: "/quotes/new", section: "quotes" },
  { id: "new-invoice", title: "New Invoice", icon: "➕", route: "/invoices/new", section: "invoices" },
  { id: "new-expense", title: "New Expense", icon: "➕", route: "/expenses/new", section: "expenses" },
];

export const SECTION_IDS = SECTIONS.map((s) => s.id);

export function findEntry(id) {
  return SECTIONS.find((s) => s.id === id);
}

// Which section does this pathname belong to? Longest matching route wins so a
// nested path always resolves to its section; falls back to Dashboard.
export function findSectionForPath(pathname) {
  const matches = SECTIONS.filter(
    (s) => pathname === s.route || pathname.startsWith(s.route + "/")
  ).sort((a, b) => b.route.length - a.route.length);
  return matches[0] || SECTIONS[0];
}

// Sections visible to this user (Admin is admin-only).
export function getVisibleSections(isAdmin) {
  return SECTIONS.filter((s) => !s.adminOnly || isAdmin);
}

const SINGULAR = { quotes: "Quote", invoices: "Invoice", expenses: "Expense" };

// Pure pathname -> window-title mapping, available synchronously on route change
// (no data fetch, no title flash). Pages can refine it further via useWindowTitle.
export function defaultTitleForPath(pathname) {
  const section = findSectionForPath(pathname);
  if (section.id === "orders" && pathname === "/orders/completed") return "Completed Orders";
  const singular = SINGULAR[section.id];
  if (singular) {
    if (pathname.endsWith("/new")) return "New " + singular;
    if (pathname.endsWith("/edit")) return "Edit " + singular;
    if (pathname !== section.route) return "View " + singular;
  }
  return section.title;
}
